import create, { GetState, SetState } from 'zustand';
import { devtools } from 'zustand/middleware';

import { SupportedSubjects } from 'types/Subject';

import { resolveSheetsTimerDuration, saveSheetsTimerDuration } from 'uniqueComponents/Sheets/SheetsTimer/sheetsTimerPersist';

const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

type SheetsTimerStore = {
  durationMinutes: number | null;
  elapsedSeconds: number;
  activeSheetKey: string | null;
  isRunning: boolean;
  subjectCode: SupportedSubjects | null;

  initForSubject: (subjectCode: SupportedSubjects) => void;
  setDurationMinutes: (durationMinutes: number | null) => void;
  startForSheet: (sheetKey: string) => void;
  stop: () => void;
  tick: () => void;
};

const sheetsTimerStore = (set: SetState<SheetsTimerStore>, get: GetState<SheetsTimerStore>) => ({
  durationMinutes: null,
  elapsedSeconds: 0,
  activeSheetKey: null,
  isRunning: false,
  subjectCode: null,

  initForSubject: (subjectCode: SupportedSubjects) => {
    set({
      subjectCode,
      durationMinutes: resolveSheetsTimerDuration(subjectCode),
      elapsedSeconds: 0,
      activeSheetKey: null,
      isRunning: false,
    });
  },

  setDurationMinutes: (durationMinutes: number | null) => {
    const { subjectCode, activeSheetKey } = get();
    if (subjectCode) {
      saveSheetsTimerDuration(subjectCode, durationMinutes);
    }

    set({
      durationMinutes,
      elapsedSeconds: 0,
      isRunning: durationMinutes != null && activeSheetKey != null,
    });
  },

  startForSheet: (sheetKey: string) => {
    const { activeSheetKey } = get();
    if (activeSheetKey === sheetKey) {
      return;
    }

    set({
      activeSheetKey: sheetKey,
      elapsedSeconds: 0,
      isRunning: true,
    });
  },

  stop: () => {
    set({
      activeSheetKey: null,
      elapsedSeconds: 0,
      isRunning: false,
    });
  },

  tick: () => {
    const { isRunning } = get();
    if (!isRunning) {
      return;
    }

    set(state => ({ elapsedSeconds: state.elapsedSeconds + 1 }));
  },
});

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useSheetsTimerStore = create(devtools(sheetsTimerStore, { name: 'SHEETS_TIMER', enabled: !isProduction }));

export default useSheetsTimerStore;
